import { Tooltip } from "antd";
import type { ColumnType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import { formatDeliveredAt } from "./ultis";

/**
 * The columns both order lists share. They live apart from common.tsx so a test
 * can render them without dragging in the Commons barrel (its antd/es imports
 * don't survive Jest's transform).
 */

/** What every order-list cell shows when there is no value behind it. */
export const EMPTY_CELL = "--";

/**
 * A cell that never grows the table: it stays on one line, cuts off with an
 * ellipsis, and gives the whole value back on hover. `ellipsis` is what clips
 * it; `showTitle: false` stops the browser's own title tooltip from fighting
 * AntD's.
 */
const oneLine = (value: string) =>
      value ? (
            <Tooltip placement="topLeft" title={value}>
                  <span>{value}</span>
            </Tooltip>
      ) : (
            EMPTY_CELL
      );

/**
 * Order number. The codes ("ORD-20260825-32E9145B") are long enough to wrap a
 * row into two lines, so the column is fixed-width and clipped.
 *
 * Shared by both order lists — tasking and monitoring show the same column, and
 * defining it once is what keeps them that way.
 */
export const orderNumberColumn = (t: any): ColumnType<any> => ({
      title: t("Order number"),
      dataIndex: "order_number",
      key: "order_number",
      width: 190,
      ellipsis: { showTitle: false },
      render: oneLine,
});

/**
 * Order name. Free-typed, so it can be arbitrarily long — left without a width
 * so it takes whatever room the fixed columns leave, and clipped there rather
 * than pushing the table wider than the window.
 */
export const orderNameColumn = (t: any): ColumnType<any> => ({
      title: t("Order Name"),
      dataIndex: "name",
      key: "name",
      // A width the other columns have to leave room for, rather than "whatever
      // is left" — on the monitoring list, which carries an extra category
      // column, that leftover came to about one word.
      width: 240,
      ellipsis: { showTitle: false },
      render: oneLine,
});

/**
 * Time the last image was handed over. Orders that haven't delivered anything
 * yet — most of them — show a dash rather than an empty cell.
 */
export const deliveredAtColumn = (
      t: any,
      /** Controlled so the arrow matches the sort the URL is carrying. */
      sortOrder: SortOrder = null,
): ColumnType<any> => ({
      title: t("Image delivered at"),
      dataIndex: "last_image_delivered_at",
      key: "last_image_delivered_at",
      sorter: true,
      sortOrder,
      // "11:08 26/08/2026" needs nowhere near the 250 the older date column asks
      // for; the slack is better spent on the order name.
      width: 180,
      render: (value: unknown) => formatDeliveredAt(value) || EMPTY_CELL,
});
